import type { Flow, MutableFlow } from '../contract/flow';
import type { FlowSubscription } from '../contract/subscription';
import { isBypassSsr } from '../utils/bypassSsrCheck';
import { isServer } from '../utils/isServer';
import { bumpFlowRevision } from '../utils/flowRevision';

/**
 * Внутренний объект подписки, расширяющий публичный интерфейс FlowSubscription.
 *
 * Этот интерфейс используется внутри MutableFlowImpl для хранения как публичного
 * метода unsubscribe, так и приватной функции listener для каждой подписки.
 *
 * @internal
 */
export interface Subscription extends FlowSubscription {
    /**
     * Функция listener, которая будет вызвана при изменении значения потока.
     */
    listener: () => void;
}

/**
 * Реализация MutableFlow.
 *
 * @typeParam Data - Тип данных, хранящихся в потоке
 */
export class MutableFlowImpl<Data> implements MutableFlow<Data> {
    /**
     * Текущее значение, хранящееся в потоке.
     */
    protected value: Data;

    /**
     * Набор функций, которые вызываются при изменении значения.
     */
    protected subscriptions: Set<Subscription>;

    /**
     * Создает новый экземпляр MutableFlowImpl с заданным начальным значением.
     *
     * @param initialValue - Начальное значение для хранения в потоке
     */
    public constructor(initialValue: Data) {
        this.value = initialValue;
        this.subscriptions = new Set();
    }

    /**
     * Подписывается на изменения в потоке.
     *
     * Функция listener будет вызываться синхронно при каждом изменении значения потока
     * через метод {@link emit}. Listener не получает параметров и
     * должен использовать {@link getSnapshot} для доступа к текущему значению.
     *
     * @param listener - Callback-функция, которая будет вызываться при изменениях значения
     * @returns Объект подписки, который можно использовать для отписки от изменений
     *
     * @example
     * ```typescript
     * const flow = createFlow(0);
     *
     * const subscription = flow.subscribe(() => {
     *   console.log('New value:', flow.getSnapshot());
     * });
     *
     * flow.emit(1); // Вызывает listener
     * subscription.unsubscribe(); // Остановить прослушивание изменений
     * ```
     */
    public subscribe(listener: () => void): FlowSubscription {
        if (isServer() && !isBypassSsr()) {
            throw new Error('Server-side flow subscriptions are not supported');
        }

        const subscription: Subscription = {
            listener,
            unsubscribe: () => {
                const subs = new Set(this.subscriptions);
                subs.delete(subscription);
                this.subscriptions = subs;
            },
        };

        const subs = new Set(this.subscriptions);
        subs.add(subscription);
        this.subscriptions = subs;

        return subscription;
    }

    /**
     * Отправляет новое значение в поток.
     *
     * Этот метод обновляет внутреннее состояние и вызывает всех зарегистрированных подписчиков
     * синхронно.
     *
     * @param value - Новое значение
     *
     * @example
     * ```typescript
     * const flow = createFlow(0);
     *
     * flow.subscribe(() => {
     *   console.log('Updated to:', flow.getSnapshot());
     * });
     *
     * flow.emit(42); // Выводит: "Updated to: 42"
     * flow.emit(100); // Выводит: "Updated to: 100"
     * ```
     */
    public emit(value: Data): void {
        if (this.value === value) {
            return;
        }

        this.value = value;
        bumpFlowRevision();

        const errors: unknown[] = [];

        for (const subscription of this.subscriptions) {
            try {
                subscription.listener();
            } catch (error) {
                errors.push(error);
            }
        }

        if (errors.length > 0) {
            throw new AggregateError(errors, 'Failed to call flow listeners');
        }
    }

    /**
     * Возвращает текущее значение потока.
     *
     * Этот метод предоставляет синхронный доступ к текущему значению без
     * подписки на изменения. Его можно безопасно вызывать в любое время, и он всегда
     * вернет самое последнее значение.
     *
     * @returns Текущее значение
     *
     * @example
     * ```typescript
     * const flow = createFlow("initial");
     *
     * console.log(flow.getSnapshot()); // "initial"
     *
     * flow.emit("updated");
     * console.log(flow.getSnapshot()); // "updated"
     * ```
     */
    public getSnapshot(): Data {
        return this.value;
    }

    /**
     * Возвращает текущее значение потока: оно есть с момента создания, поэтому признака
     * «значение еще не материализовалось» в возвращаемом типе нет. Само значение при этом
     * вполне может быть `undefined` — например, у `createFlow<number | undefined>(undefined)`.
     *
     * @returns Текущее значение
     */
    public getCachedSnapshot(): Data {
        return this.value;
    }

    /**
     * Преобразует этот MutableFlow в readonly Flow.
     *
     * Возвращаемый Flow предоставляет те же реактивные возможности,
     * но без возможности отправлять новые значения. Это полезно для предоставления
     * readonly доступа к значению потока при сохранении контроля над мутациями.
     *
     * @returns Readonly Flow
     *
     * @example
     * ```typescript
     * const mutableFlow = createFlow(0);
     * const readOnlyFlow = mutableFlow.asFlow();
     *
     * // Можно читать и подписываться
     * console.log(readOnlyFlow.getSnapshot()); // 0
     * readOnlyFlow.subscribe(() => console.log('Changed!'));
     *
     * // Нельзя мутировать
     * // readOnlyFlow.emit(1); // Ошибка TypeScript
     *
     * // Но оригинал все еще может изменять поток
     * mutableFlow.emit(1); // Вызывает подписчика
     * ```
     */
    public asFlow(): Flow<Data> {
        return this;
    }
}

/**
 * Создает новый MutableFlow с указанным начальным значением.
 *
 * @typeParam Data - Тип данных, которые будут храниться в потоке
 * @param initialValue - Начальное значение для хранения в потоке
 * @returns Новый экземпляр MutableFlow, инициализированный заданным значением
 *
 * @example
 * ```typescript
 * // Создать потоки с разными типами данных
 * const numberFlow = createFlow(0);
 * const stringFlow = createFlow("hello");
 * const booleanFlow = createFlow(true);
 * const objectFlow = createFlow({ count: 0, name: "example" });
 *
 * // Подписка на изменения
 * numberFlow.subscribe(() => {
 *   console.log('Number changed to:', numberFlow.getSnapshot());
 * });
 *
 * numberFlow.emit(42); // Выводит: "Number changed to: 42"
 * ```
 *
 * @example
 * ```typescript
 * // Создание потока
 * const counter = createFlow(0);
 *
 * const increment = () => {
 *   const current = counter.getSnapshot();
 *   counter.emit(current + 1);
 * };
 *
 * counter.subscribe(() => {
 *   console.log(`Counter: ${counter.getSnapshot()}`);
 * });
 *
 * increment(); // Выводит: "Counter: 1"
 * increment(); // Выводит: "Counter: 2"
 * ```
 */
export function createFlow<Data>(initialValue: Data): MutableFlow<Data> {
    return new MutableFlowImpl(initialValue);
}
